import { useTranslation } from "react-i18next";
import { Coins, Users, Wrench } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { IssueIcon, thematicClasses, type ThematicClasses } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import { useSetActionSelection } from "../../api/hooks";
import type {
  ActionPlanAction,
  ActionPlanDetail,
  ActionPlanIssue,
  ActionPlanThematic,
} from "../../api/types";
import {
  balanceColumns,
  isIssueIncluded,
  issueActions,
  issueStats,
  listedIssues,
  planCoverage,
  thematicStats,
} from "../../lib/selection";
import { StakeholderGroupsCard } from "../StakeholderGroupsCard";
import { Checkbox } from "../ui/Checkbox";
import { StepIntro } from "./StepIntro";

/** A selection write: flip `is_selected` on a set of actions. */
type Toggle = (actions: ActionPlanAction[], selected: boolean) => void;

/** Fraction 0–1, guarding the empty-thematic case. */
function ratio(part: number, whole: number): number {
  return whole === 0 ? 0 : part / whole;
}

/**
 * The experts / tools / financial aids available under an issue, each gated on
 * the enterprise having that directory: a count for a directory they cannot open
 * would be an empty promise. Renders nothing when no directory has anything.
 */
function IssueResources({
  plan,
  stats,
}: {
  plan: ActionPlanDetail;
  stats: { experts: number; tools: number; aids: number };
}) {
  const { t } = useTranslation();
  const entries = [
    {
      key: "experts",
      Icon: Users,
      count: stats.experts,
      enabled: plan.expert_directory_enabled,
      label: t("actionPlans.issues.resources.experts", { count: stats.experts }),
    },
    {
      key: "tools",
      Icon: Wrench,
      count: stats.tools,
      enabled: plan.tool_directory_enabled,
      label: t("actionPlans.issues.resources.tools", { count: stats.tools }),
    },
    {
      key: "aids",
      Icon: Coins,
      count: stats.aids,
      enabled: plan.financial_aid_directory_enabled,
      label: t("actionPlans.issues.resources.aids", { count: stats.aids }),
    },
  ].filter((e) => e.enabled && e.count > 0);

  if (entries.length === 0) return null;

  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-7 text-xs text-muted-foreground">
      {entries.map(({ key, Icon, label }) => (
        <span key={key} className="inline-flex items-center gap-1 tabular-nums">
          <Icon className="size-3 shrink-0" aria-hidden />
          {label}
        </span>
      ))}
    </p>
  );
}

/**
 * One issue: include toggle, name, and the numbers on its tile in the other
 * app — actions already completed — plus what including it brings: objectives
 * and courses of action. Counts leave the member's own actions out.
 */
function IssueRow({
  issue,
  plan,
  tc,
  toggle,
}: {
  issue: ActionPlanIssue;
  plan: ActionPlanDetail;
  tc: ThematicClasses;
  toggle: Toggle;
}) {
  const { t } = useTranslation();
  const stats = issueStats(issue);
  const included = isIssueIncluded(issue);
  const actions = issueActions(issue);

  return (
    <div className={cn("border-t border-border/70 px-3 py-1", included && tc.softBg)}>
      <div className="flex items-center gap-3">
        <Checkbox
          checked={included}
          disabled={actions.length === 0}
          onCheckedChange={(v) => toggle(actions, v)}
          label={issue.name}
          leading={
            <IssueIcon icon={issue.icon} className={cn("size-4 shrink-0", tc.ink)} aria-hidden />
          }
          className={cn(
            // Dense by design: the row itself is the click target across its
            // full width, and 28px still clears the WCAG 2.5.8 (AA) 24px floor
            // — the same trade the `sm` button size documents.
            "min-h-0 min-w-0 flex-1 gap-2.5 py-1 text-[13px]",
            included && "font-semibold",
          )}
        />
        <span
          className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
          title={t("actionPlans.issues.completedActions", {
            count: issue.completed_action_count,
          })}
        >
          {issue.completed_action_count}
        </span>
        <span
          className="w-8 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
          title={t("actionPlans.issues.goalsCount", { count: stats.goals })}
        >
          {stats.goals}
        </span>
        {/* Always the issue's own size. Including an issue takes all of its
            courses of action, so a retained/total split here only ever read
            "4/4" — the number that means something is how much the issue
            brings. Partial selection belongs to step 2, which is where it can
            be changed. */}
        <span
          className={cn(
            "w-11 shrink-0 text-right text-xs tabular-nums",
            included ? "font-semibold" : "text-muted-foreground",
          )}
          title={t("actionPlans.issues.availableActions", { count: stats.actions })}
        >
          {stats.actions}
        </span>
      </div>
      {/* Only for included issues: help available on what you've actually taken
          on, rather than a directory census over the whole catalogue. */}
      {included && <IssueResources plan={plan} stats={stats} />}
    </div>
  );
}

/** One thematic: coloured header with its own include-all toggle, then its issues. */
function ThematicBlock({
  thematic,
  plan,
  toggle,
}: {
  thematic: ActionPlanThematic;
  plan: ActionPlanDetail;
  toggle: Toggle;
}) {
  const { t } = useTranslation();
  const tc = thematicClasses(thematic.css_class);
  const stats = thematicStats(thematic);
  const allIncluded = stats.includedIssues === stats.issues && stats.issues > 0;
  // The issue toggles still move every action beneath the issue, the member's
  // own included — the server-rendered step does the same.
  const issues = listedIssues(thematic);
  const actions = issues.flatMap(issueActions);

  return (
    <section className="overflow-hidden rounded-lg border bg-card">
      <header
        className={cn(
          "flex items-center gap-2.5 px-3 py-2 text-[13px] font-bold",
          tc.softBg,
          tc.ink,
        )}
      >
        <span aria-hidden className={cn("size-2.5 shrink-0 rounded-sm", tc.solid)} />
        <span className="min-w-0 flex-1 truncate">{thematic.name}</span>
        <span className="shrink-0 text-[11px] tabular-nums opacity-80">
          {t("actionPlans.issues.includedOf", {
            included: stats.includedIssues,
            total: stats.issues,
          })}
        </span>
        <button
          type="button"
          disabled={actions.length === 0}
          onClick={() => toggle(actions, !allIncluded)}
          aria-label={
            allIncluded
              ? t("actionPlans.issues.clearThematicAria", { thematic: thematic.name })
              : t("actionPlans.issues.selectThematicAria", { thematic: thematic.name })
          }
          className="shrink-0 text-[11px] font-bold underline underline-offset-2 hover:no-underline disabled:opacity-50"
        >
          {allIncluded
            ? t("actionPlans.issues.clearShort")
            : t("actionPlans.issues.selectShort")}
        </button>
      </header>
      {/* Column legend: the two numbers on each row would otherwise be unlabelled. */}
      <div className="flex items-center gap-3 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        <span className="min-w-0 flex-1">{t("actionPlans.issues.colIssue")}</span>
        <span className="w-20 shrink-0 text-right">
          {t("actionPlans.issues.colCompleted")}
        </span>
        <span className="w-8 shrink-0 text-right">{t("actionPlans.issues.colGoals")}</span>
        <span className="w-11 shrink-0 text-right">{t("actionPlans.issues.colActions")}</span>
      </div>
      {issues.map((issue) => (
        <IssueRow
          key={issue.id}
          issue={issue}
          plan={plan}
          tc={tc}
          toggle={toggle}
        />
      ))}
    </section>
  );
}

/** A labelled coverage bar — used per thematic inside the summary rail. */
function CoverageBar({
  thematic,
}: {
  thematic: ActionPlanThematic;
}) {
  const tc = thematicClasses(thematic.css_class);
  const stats = thematicStats(thematic);
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className={cn("truncate text-[11px] font-bold uppercase tracking-wide", tc.ink)}>
          {thematic.name}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {stats.selected} / {stats.actions}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full", tc.solid)}
          style={{ width: `${ratio(stats.selected, stats.actions) * 100}%` }}
        />
      </div>
    </div>
  );
}

/** A single headline number in the rail. */
function RailStat({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <b className="text-2xl font-bold leading-none tracking-tight tabular-nums">{value}</b>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

/**
 * Step 1 — issue selection. There's no writable issue.is_selected endpoint, so
 * "including" an issue selects (or clears) every action beneath it through the
 * action API, and the checkbox state is derived from those actions.
 *
 * Laid out as two columns of dense thematic blocks with a sticky summary rail:
 * the plan can carry ~30 issues, and one full-width row each ran past two
 * screens with a single number on it.
 */
export function IssuesStep({ plan }: { plan: ActionPlanDetail }) {
  const { t } = useTranslation();
  const setSelection = useSetActionSelection(plan.id);

  // Placeholder thematics with no listed issue would render an empty block.
  const thematics = plan.action_plan_thematics.filter(
    (th) => listedIssues(th).length > 0,
  );
  const coverage = planCoverage(plan);
  const allActions = thematics.flatMap((th) =>
    listedIssues(th).flatMap(issueActions),
  );

  // One request for the whole set, and only for the actions that actually
  // change — including an issue can cover 84 of them.
  const toggle: Toggle = (actions, selected) => {
    const changed = actions.filter((a) => Boolean(a.is_selected) !== selected);
    if (changed.length === 0) return;
    setSelection.mutate({
      actionIds: changed.map((a) => a.id),
      isSelected: selected,
    });
  };

  const [leftColumn, rightColumn] = balanceColumns(
    thematics,
    (th) => listedIssues(th).length,
  );

  if (thematics.length === 0) {
    return (
      <div className="space-y-6">
        <StepIntro
          heading={t("actionPlans.issues.heading")}
          help={t("actionPlans.issues.help")}
        />
        <Card>
          <CardContent className="space-y-1 py-6">
            <p className="font-medium">{t("actionPlans.issues.empty")}</p>
            <p className="text-sm text-muted-foreground">
              {t("actionPlans.issues.emptyHint")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <StepIntro
            heading={t("actionPlans.issues.heading")}
            help={t("actionPlans.issues.help")}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={coverage.selected === coverage.actions}
            onClick={() => toggle(allActions, true)}
          >
            {t("actionPlans.issues.selectAll")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={coverage.selected === 0}
            onClick={() => toggle(allActions, false)}
          >
            {t("actionPlans.issues.clearAll")}
          </Button>
        </div>
      </div>

      {/* minmax(0,…) at every breakpoint: an `auto` track sizes to its content's
          min-content, and a truncating (nowrap) thematic name reports its whole
          string there — enough to push the single-column layout wider than the
          viewport. */}
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_17rem] lg:items-start">
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2 md:items-start">
          {[leftColumn, rightColumn].map((column, index) => (
            <div key={index} className="space-y-4">
              {column.map((thematic) => (
                <ThematicBlock
                  key={thematic.id}
                  thematic={thematic}
                  plan={plan}
                  toggle={toggle}
                />
              ))}
            </div>
          ))}
        </div>

        {/* Summary rail: the balance-across-pillars question answered without
            scrolling back up. Sticky on wide screens, stacked under the blocks
            on narrow ones. */}
        <aside className="lg:sticky lg:top-4">
          <Card>
            <CardContent className="space-y-4 p-4">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {t("actionPlans.issues.summaryTitle")}
              </h3>
              <div className="space-y-2.5">
                <RailStat
                  value={coverage.includedIssues}
                  label={t("actionPlans.issues.summaryIssues", {
                    total: coverage.issues,
                  })}
                />
                <RailStat
                  value={coverage.selected}
                  label={t("actionPlans.issues.summaryActions", {
                    total: coverage.actions,
                  })}
                />
                <RailStat
                  value={coverage.goalsCovered}
                  label={t("actionPlans.issues.summaryGoals")}
                />
              </div>
              <div className="space-y-2.5 border-t pt-3">
                {thematics.map((thematic) => (
                  <CoverageBar key={thematic.id} thematic={thematic} />
                ))}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Where the Django page asks for them ("3. Définissez les catégories de
          parties prenantes à consulter"), so step 2 opens with something to
          assign. Still editable from step 2's consultation modal. */}
      <StakeholderGroupsCard />
    </div>
  );
}
